import type { AdaptiveFormProvider } from "@kotaio/adaptive-form/react";
import type { ComponentProps } from "react";

type RequirementsObject = ComponentProps<typeof AdaptiveFormProvider>["requirements"];

export type ParseResult =
  | { success: true; requirements: RequirementsObject }
  | { success: false; error: string };

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function parseAndValidateRequirements(rawJson: string): ParseResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawJson);
  } catch {
    return { success: false, error: "Invalid JSON syntax" };
  }

  if (!isObject(parsed)) {
    return { success: false, error: "JSON must be an object" };
  }

  const errors: string[] = [];
  const fields = parsed["fields"];

  if (!Array.isArray(fields)) {
    errors.push("fields: Expected fields to be an array");
  } else {
    for (let i = 0; i < fields.length; i += 1) {
      const field = fields[i] as unknown;
      if (!isObject(field)) {
        errors.push(`fields[${i}]: Expected field to be an object`);
      } else {
        if (typeof field["id"] !== "string") {
          errors.push(`fields[${i}].id: Expected string`);
        }
        if (typeof field["type"] !== "string") {
          errors.push(`fields[${i}].type: Expected string`);
        }
      }
    }
  }

  if (errors.length > 0) {
    return { success: false, error: errors.join("; ") };
  }

  return { success: true, requirements: parsed as unknown as RequirementsObject };
}
